// AWS mode (F3): replaces the @nuxtjs/supabase client with a facade that
// speaks Cognito (auth) + PostgREST-on-EC2 (data) — stores stay untouched.
// Enabled ONLY when NUXT_PUBLIC_AWS_AUTH_ENABLED=true; otherwise this plugin
// is a no-op and the app runs on Supabase exactly as before.
//
// Runs AFTER the @nuxtjs/supabase module plugin (module plugins are
// prepended; app plugins run later), so $supabase.client already exists.
import { PostgrestClient } from '@supabase/postgrest-js'
import { RealtimeClient } from '@supabase/realtime-js'
import { createCognitoAuth, purgeAbandonedSupabaseSession } from '~/utils/cognitoAuth'
import { createAuthedFetch } from '~/utils/authedFetch'

export default defineNuxtPlugin({
  name: 'aws-auth-facade',
  async setup (nuxtApp) {
    const pub = useRuntimeConfig().public
    if (!pub.awsAuthEnabled) return

    // El cliente real de @nuxtjs/supabase ya se construyó (su plugin corre
    // antes que este) y, si quedaron cookies `sb-*` de la era Supabase, las
    // refresca solo en cada carga contra el proyecto abandonado: en staging eso
    // devolvía un token VIVO de otro usuario y otra clínica (2026-08-07).
    // Se apaga el auto-refresh y se retira esa sesión ANTES de sustituir el
    // cliente, para que ese request no vuelva a salir.
    //
    // G-276 — POR QUÉ NO SE USA `auth.signOut({ scope: 'local' })` AQUÍ:
    // GoTrue abre un `BroadcastChannel` con nombre = su storageKey y difunde
    // TODOS sus eventos a las demás pestañas del mismo origen
    // (@supabase/auth-js GoTrueClient: `_notifyAllSubscribers(..., broadcast)`).
    // `signOut` termina en `_removeSession()` → `SIGNED_OUT` → difundido. Y el
    // plugin del módulo (@nuxtjs/supabase/runtime/plugins/supabase.client) tiene
    // un `onAuthStateChange` permanente sobre ESE cliente que escribe
    // `useSupabaseSession()`/`useSupabaseUser()`.
    //
    // Resultado en staging (demo Haus, 2026-09-04): abrir CUALQUIER segunda
    // pestaña de la app —imprimir receta, orden de laboratorio, antecedentes en
    // pestaña nueva— hacía que la pestaña nueva difundiera `SIGNED_OUT` y que la
    // pestaña original se quedara con `useSupabaseUser() === null` aun con la
    // sesión de Cognito intacta. A la siguiente navegación,
    // 02.permission.global.ts la mandaba a /auth/login. Seis expulsiones en 2.5 h,
    // con CERO 401 en el ALB: el backend nunca rechazó nada.
    const original = (nuxtApp.$supabase as any)?.client
    try {
      original?.auth?.stopAutoRefresh?.()
      // Que este cliente muerto tampoco RECIBA difusiones de otras pestañas
      // (p. ej. una pestaña vieja que todavía corre el build anterior).
      original?.auth?.broadcastChannel?.close?.()
    } catch { /* si el cliente ya no responde, el purge de abajo alcanza */ }
    // Mismo borrado que hacía `signOut({ scope: 'local' })` (cookies + llaves
    // `sb-*` de localStorage), pero sin pasar por GoTrue → sin difusión.
    // NO toca las llaves de Cognito: borrarlas al arrancar dejaría al usuario
    // fuera en cada carga. Ese barrido va en el signOut del adaptador.
    purgeAbandonedSupabaseSession()

    // Un solo origen: datos, funciones y WS salen del mismo host. awsFunctionsUrl
    // solo existe para el estado PREVIO al cutover, cuando las funciones vivían en
    // API Gateway y los datos en PostgREST. Vacío = usar awsApiUrl, que tras T1 es
    // el BE y sirve `/functions/v1/*` él mismo.
    const apiBase = pub.awsApiUrl as string
    const fnsBase = (pub.awsFunctionsUrl as string) || apiBase

    const adapter = createCognitoAuth({
      poolId: pub.cognitoPoolId as string,
      clientId: pub.cognitoClientId as string,
      functionsUrl: fnsBase || undefined,
    })

    // Every data request carries the (auto-refreshed) Cognito ID token, and a
    // 401 gets ONE forced refresh + retry before it reaches the stores (G-277).
    const authedFetch = createAuthedFetch({
      getToken: () => adapter.getValidIdToken(),
      refreshToken: () => adapter.refreshIdToken(),
    })

    const rest = new PostgrestClient(apiBase, { fetch: authedFetch })

    // functions/storage: explicit rejected promise — stores' try/catch surfaces
    // a clear message until F5/F6.
    const unsupported = (ns: string) => () =>
      Promise.reject(new Error(`[aws-mode] supabase.${ns} no disponible aún (F5 functions / F6 storage)`))

    // F4: real Realtime client against the self-hosted container (WS via Caddy).
    // The tenant validates Cognito ID tokens through its JWKS. CRITICAL: the WS
    // HANDSHAKE validates the `apikey` QUERY PARAM as a JWT (the accessToken
    // callback only authenticates channels post-connect), so apikey must be a
    // live ID token — seeded after session hydration below and refreshed on
    // every TOKEN_REFRESHED via the mutable params object.
    // Non-empty placeholder: the client THROWS on empty apikey at construction.
    // The real ID token replaces it during the blocking hydration below, before
    // any store subscribes (first WS handshake carries the real token).
    // UNA sola variable a propósito: el WS sale del MISMO origen que los datos.
    //
    // Eso no significa que los WebSockets pasen por el proceso Hono — no pasan. El
    // ALB reparte por path: `/realtime/v1/*` va al contenedor de Realtime y todo lo
    // demás al BE. El corte lo hace el balanceador, igual que ya lo hacía Caddy en
    // `api-staging`. Verificado: ese path responde `server: Cowboy` (Phoenix), no
    // Hono.
    //
    // Derivarlo de awsApiUrl fue un bug mientras el BE y Realtime vivían en hosts
    // distintos; con un solo origen vuelve a ser correcto, y evita arrastrar dos
    // variables que pueden desincronizarse.
    const realtimeBase = apiBase.replace(/^https/, 'wss')

    const realtimeParams: Record<string, string> = { apikey: 'pending-auth' }
    const realtime = new RealtimeClient(
      `${realtimeBase}/realtime/v1`,
      {
        params: realtimeParams,
        accessToken: async () => (await adapter.getValidIdToken()) ?? '',
      },
    )

    // F5: functions served by domain Lambdas behind API Gateway. Response shape
    // mirrors supabase-js FunctionsClient ({ data, error }).
    const invoke = async (name: string, opts: { body?: unknown, headers?: Record<string, string> } = {}) => {
      if (!fnsBase) return unsupported('functions.invoke')()
      try {
        const res = await authedFetch(`${fnsBase}/functions/v1/${name}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...(opts.headers ?? {}) },
          body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        })
        const text = await res.text()
        let data: unknown = null
        try { data = text ? JSON.parse(text) : null } catch { data = text }
        if (!res.ok) {
          return { data: null, error: Object.assign(new Error(`fn ${name}: ${res.status}`), { context: data, status: res.status }) }
        }
        return { data, error: null }
      } catch (e: any) {
        return { data: null, error: e }
      }
    }

    const facade = {
      from: (relation: string) => rest.from(relation),
      rpc: (fn: string, args?: Record<string, unknown>, options?: Record<string, unknown>) =>
        (rest.rpc as any)(fn, args, options),
      schema: (s: string) => rest.schema(s),
      auth: adapter,
      functions: { invoke },
      storage: { from: () => ({ upload: unsupported('storage.upload'), download: unsupported('storage.download'), getPublicUrl: () => ({ data: { publicUrl: '' } }), createSignedUrl: unsupported('storage.createSignedUrl') }) },
      channel: (name: string, opts?: Record<string, unknown>) => realtime.channel(name, opts as any),
      removeChannel: (ch: any) => realtime.removeChannel(ch),
      removeAllChannels: () => realtime.removeAllChannels(),
      realtime,
    }

    ;(nuxtApp.$supabase as any).client = facade

    // Feed the module's session/user state so useSupabaseUser(),
    // 02.permission.global.ts and the module's auth-redirect keep working.
    const sessionState = useState<any>('supabase_session')
    const userState = useState<any>('supabase_user')
    // Última sesión que Cognito dio por buena. En modo AWS es la ÚNICA fuente
    // válida de `useSupabaseUser()`; se guarda para poder re-imponerla si algo
    // ajeno la pisa (G-276).
    let cognitoSession: any = null
    const sync = (session: any) => {
      cognitoSession = session
      sessionState.value = session
      userState.value = session?.user ?? null
    }
    adapter.onAuthStateChange((event, session) => {
      sync(session)
      // keep the live WS authenticated with the fresh token (handshake param
      // for reconnects + channel-level auth for the open socket)
      if (session?.access_token) {
        realtimeParams.apikey = session.access_token
        realtime.setAuth(session.access_token)
      }
      if (event === 'SIGNED_OUT') {
        realtimeParams.apikey = ''
        realtime.removeAllChannels()
      }
    })
    // BLOCKING initial hydration: the module's global auth-redirect middleware
    // reads useSupabaseSession() on the very first route — if we hydrate async,
    // a logged-in user gets bounced to /auth/login on every hard reload.
    const { data } = await adapter.getSession()
    sync(data.session)
    if (data.session?.access_token) realtimeParams.apikey = data.session.access_token

    // G-276 (defensa en profundidad): el cliente Supabase muerto sigue con el
    // `onAuthStateChange` del módulo enganchado, y todavía puede emitir por su
    // cuenta —su `initialize()`/`_recoverAndRefresh()` corre antes que este
    // plugin y puede terminar en `SIGNED_OUT` después de nuestra hidratación—.
    // Como el módulo se registró primero, su callback escribe null y el nuestro
    // corre después y vuelve a poner la sesión de Cognito. En modo AWS ese
    // cliente NO manda sobre la identidad: la única fuente es el adaptador.
    try {
      original?.auth?.onAuthStateChange?.(() => {
        if (userState.value !== (cognitoSession?.user ?? null)) sync(cognitoSession)
      })
    } catch { /* cliente muerto sin superficie de auth: nada que reafirmar */ }
  },
})
